import MarkdownIt from 'markdown-it';
import { memo, useMemo } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature } from '@treams/treams-hooks';
import {
	AgentChatMessageFragment,
	AgentChatMessageRole,
	AgentChatToolCallFragment,
	FeatureName,
} from '@treams/treams-network';
import { Chat, MarkdownStyled, chatSlideIn, tokens } from '@treams/treams-ui';

import { AnalyticsBlock, AnalyticsBlockSkeleton, AnalyticsTextSkeleton } from './AnalyticsBlock';

const markdown = new MarkdownIt({ breaks: true, linkify: true });

// Contract with the model (documented in the analytics authoring guide): once
// this exact heading appears (on its own line), the rest of the response is a
// visualization response, and every fenced code block after it is an
// analytics spec. Content before the heading is regular prose.
const ANALYTICS_SENTINEL = '# Treams Analytics';

/**
 * Finds the sentinel heading anywhere in the content, as long as it sits at
 * the start of a line (position 0 or right after a newline).
 * @param content The full message content.
 * @returns The index of the sentinel, or -1 when absent.
 */
function findAnalyticsSentinelIndex(content: string): number {
	let index = content.indexOf(ANALYTICS_SENTINEL);

	while (index > 0 && content[index - 1] !== '\n') {
		index = content.indexOf(ANALYTICS_SENTINEL, index + 1);
	}

	return index;
}

type AnalyticsSegment =
	| { type: 'prose'; text: string }
	| { type: 'spec'; text: string }
	| { type: 'pending'; text: string };

/**
 * Whether a spec block is a text-only answer, so its placeholder can be a
 * single text line instead of the full-height block skeleton. Works on
 * partial JSON too (a fence still streaming in) by inferring from the
 * presentation keys seen so far.
 * @param specText The raw (possibly incomplete) JSON text of the spec block.
 */
function isTextOnlySpec(specText: string): boolean {
	try {
		const json = JSON.parse(specText) as Record<string, unknown>;

		return typeof json.text === 'string' && !json.grid && !json.chart;
	} catch {
		return /"text"\s*:/.test(specText) && !/"(grid|chart)"\s*:/.test(specText);
	}
}

function segmentAnalyticsContent(content: string): AnalyticsSegment[] {
	const segments: AnalyticsSegment[] = [];

	let prose: string[] = [];
	let fence: string[] | null = null;

	const flushProse = () => {
		const text = prose.join('\n').trim();

		if (text) segments.push({ type: 'prose', text });

		prose = [];
	};

	for (const line of content.split('\n')) {
		if (fence === null && line.trim().startsWith('```')) {
			flushProse();
			fence = [];
			continue;
		}

		if (fence !== null && line.trim() === '```') {
			segments.push({ type: 'spec', text: fence.join('\n') });
			fence = null;
			continue;
		}

		if (fence !== null) fence.push(line);
		else prose.push(line);
	}

	// An unterminated fence means the spec is still streaming in; keep the
	// partial content so the placeholder can already match the spec type.
	if (fence !== null) segments.push({ type: 'pending', text: fence.join('\n') });
	else flushProse();

	return segments;
}

const UserMessage = styled(Box)`
	max-width: 620px;
	animation: 0.35s cubic-bezier(0, 1, 0, 1) ${chatSlideIn};
	animation-fill-mode: backwards;
`;

const AssistantMessage = styled(Box)`
	max-width: 620px;
	width: 100%;
	color: ${tokens.text.primary};
	animation: 0.35s cubic-bezier(0, 1, 0, 1) ${chatSlideIn};
	animation-fill-mode: backwards;
`;

export function AgentChatTypingIndicator(props: { mt?: number }) {
	return (
		<Box mt={props.mt ?? 2} maxWidth={620}>
			<Chat.Typing />
		</Box>
	);
}

function AnalyticsChatBubble(props: { content: string; preamble?: string; streaming?: boolean }) {
	const segments = useMemo(() => segmentAnalyticsContent(props.content), [props.content]);

	const preambleHtml = useMemo(
		() => (props.preamble ? markdown.render(props.preamble) : ''),
		[props.preamble],
	);

	return (
		<AssistantMessage>
			{preambleHtml && <MarkdownStyled dangerouslySetInnerHTML={{ __html: preambleHtml }} />}
			{segments.map((segment, index) => {
				if (segment.type === 'prose') {
					return (
						<MarkdownStyled
							key={index}
							dangerouslySetInnerHTML={{ __html: markdown.render(segment.text) }}
						/>
					);
				}

				// Charts and grids only mount after the full answer has streamed
				// in: mid-stream reflows would otherwise make the responsive
				// ChartJS canvas resize and repaint on every token.
				if (segment.type === 'spec' && !props.streaming) {
					return <AnalyticsBlock key={index} specText={segment.text} />;
				}

				if (isTextOnlySpec(segment.text)) {
					return <AnalyticsTextSkeleton key={index} />;
				}

				return <AnalyticsBlockSkeleton key={index} />;
			})}
			{segments.length === 0 && <AnalyticsBlockSkeleton />}
		</AssistantMessage>
	);
}

export const AgentChatMessageGroup = memo(function AgentChatMessageGroup(props: {
	message: AgentChatMessageFragment;
	toolCalls: AgentChatToolCallFragment[];
	streaming?: boolean;
	mt?: number;
}) {
	const hasAdvancedAgent = useFeature(FeatureName.AdvancedAgent);
	const mt = props.mt ?? 2;
	const isAI = useMemo(
		() => [AgentChatMessageRole.Assistant, AgentChatMessageRole.Tool].includes(props.message.role),
		[props.message.role],
	);

	if (props.message.role === AgentChatMessageRole.Tool) {
		// Tool-call bubbles are temporarily hidden; render nothing so they
		// don't leave empty rows (and margins) between assistant messages.
		return null;
	}

	if (!props.message.content) {
		return null;
	}

	const content = props.message.content;

	// While streaming, the accumulated content may still be a prefix of the
	// sentinel heading; hold rendering until it matches or diverges.
	if (
		isAI &&
		content.length < ANALYTICS_SENTINEL.length &&
		ANALYTICS_SENTINEL.startsWith(content)
	) {
		return (
			<Box mt={mt} maxWidth={620}>
				<Chat.Typing />
			</Box>
		);
	}

	const sentinelIndex = isAI && hasAdvancedAgent ? findAnalyticsSentinelIndex(content) : -1;

	if (sentinelIndex !== -1) {
		// Everything before the sentinel is regular prose; hide the sentinel
		// heading itself by dropping its entire line.
		const preamble = content.slice(0, sentinelIndex).trim();
		const newlineIndex = content.indexOf('\n', sentinelIndex);
		const body = newlineIndex === -1 ? '' : content.slice(newlineIndex + 1);

		return (
			<Box mt={mt}>
				<AnalyticsChatBubble
					content={body}
					preamble={preamble || undefined}
					streaming={props.streaming}
				/>
			</Box>
		);
	}

	if (isAI) {
		return (
			<AssistantMessage mt={mt}>
				<MarkdownStyled dangerouslySetInnerHTML={{ __html: markdown.render(content) }} />
			</AssistantMessage>
		);
	}

	return (
		<Flex mt={mt} justifyContent="flex-end">
			<UserMessage>
				<Chat side="right" flexGrow={0}>
					<MarkdownStyled dangerouslySetInnerHTML={{ __html: markdown.render(content) }} />
				</Chat>
			</UserMessage>
		</Flex>
	);
});
